import { tracking } from '@iwoca/frontend-tracking-library';
import { AlertBox, Button, Icon, IwocaLogo, Link } from '@iwoca/orion';
import { Trans, useLingui } from '@lingui/react/macro';

import styles from './TrueLayerModal.module.css';
import { getAmplitudeDefaultEventProperties } from '../../../../utils/amplitudeEvents';
import BankLinksSVG from '../BankLinkSVG/BankLinkSVG';

export type TTrueLayerBank = {
  bank_id: string;
  bank_name: string;
  logo_url: string;
};

const BankConnectionView = ({
  onClickBack,
  onClose,
  selectedBank,
  bankUrlError,
  trueLayerBankUrl,
}: {
  onClickBack: () => void;
  onClose: () => void;
  selectedBank: TTrueLayerBank;
  bankUrlError: boolean;
  trueLayerBankUrl: string;
}) => {
  const { t } = useLingui();

  const handleBankConnectionTracking = () => {
    tracking.ampli.trueLayerBankConnectStarted({
      ...getAmplitudeDefaultEventProperties(),
      bankId: selectedBank?.bank_id,
      bankName: selectedBank?.bank_name,
    });
  };

  const selectedBankName = selectedBank.bank_name;

  return (
    <>
      <div className={styles.bankConnectionHeader}>
        <Button
          variant="tertiary"
          onClick={onClickBack}
          className={styles.backButton}
        >
          <Icon icon="angleLeft" />
          <Trans>Back</Trans>
        </Button>
        <div className={styles.bankConnectionLogos}>
          <IwocaLogo iconOnly className={styles.iwocaLogo} />
          <BankLinksSVG classNames={styles.bankLink} />
          <figure className={styles.bankConnectionLogo}>
            <img
              src={selectedBank?.logo_url}
              alt={selectedBank?.bank_name}
              className={styles.bankLogo}
            />
          </figure>
        </div>
      </div>
      <section className={styles.bankConsentContainer}>
        <h3>
          <strong>
            <Trans>What details am I sharing?</Trans>
          </strong>
        </h3>
        <p>
          <Trans>
            The following information will be shared with iwoca LTD:
          </Trans>
        </p>
        <ul>
          <li>
            <strong>
              <Trans>Full name</Trans>
            </strong>
          </li>
          <li>
            <strong>
              <Trans>Account number and sort code</Trans>
            </strong>
          </li>
          <li>
            <strong>
              <Trans>Account balance</Trans>
            </strong>
          </li>
          <li>
            <strong>
              <Trans>
                Transaction history, direct debits and standing orders
              </Trans>
            </strong>
          </li>
        </ul>
        <div className={styles.bankConnectionGradeContainer}>
          <div className={styles.bankGradeContainer}>
            <Icon icon="shieldCheck" className={styles.bankGradeIcon} />
            <span>
              <Trans>Bank-grade encryption</Trans>
            </span>
          </div>
          <p>
            <Trans>
              We use a partner called Truelayer, to access the information
              needed to assess your ability to repay a loan.
            </Trans>
          </p>
          <p>
            <Trans>
              Truelayer needs your permission to access your information for 90
              days.
            </Trans>
          </p>
          <p>
            <Trans>
              Truelayer is FCA regulated, and won’t share or use your personal
              data for anything else.
            </Trans>
          </p>
        </div>
        <div
          className={styles.trueLayerFooter}
          data-testid="true-layer-modal-footer"
        >
          <p>
            <small>
              <Trans>
                By tapping ‘Allow’, you agree to{' '}
                <Link
                  data-testid="terms-of-service-link"
                  href={t`https://truelayer.com/legal/enduser_tos/`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Truelayer’s Terms of Service
                </Link>{' '}
                and{' '}
                <Link
                  data-testid="privacy-policy-link"
                  href={t`https://truelayer.com/legal/privacy/`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </Link>
              </Trans>
            </small>
          </p>
        </div>
      </section>
      {bankUrlError && selectedBank && (
        <AlertBox variant="failure">
          <p className={styles.bankUrlError}>
            <Trans>
              Sorry, we ran into an issue while retrieving the link for{' '}
              {selectedBankName}. Please try again later.
            </Trans>
          </p>
        </AlertBox>
      )}
      <div className={styles.ctaContainer}>
        <Button
          data-testid="truelayer-bank-url"
          // TODO: Remove href when disabled. Doesn't work with current orion link.
          // https://www.w3.org/TR/html-aria/#example-communicate-a-disabled-link-with-aria
          href={trueLayerBankUrl}
          onClick={handleBankConnectionTracking}
          rel="noopener noreferrer"
          loading={!trueLayerBankUrl && !bankUrlError}
          disabled={bankUrlError}
          aria-disabled={bankUrlError}
          size="large"
        >
          <Trans>Allow</Trans>
        </Button>
        <Button variant="tertiary" onClick={onClose} size="large">
          <Trans>Cancel</Trans>
        </Button>
      </div>
    </>
  );
};

export default BankConnectionView;
